import { Node, Edge } from '@xyflow/react';

export type ServiceNodeData = {
  label: string;
  subtitle: string;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'gray';
  description: string;
  techStack: string[];
  endpoints: { label: string; href: string }[];
  designDocs: { label: string; href: string }[];
  repoUrl: string;
};

export type ArchNode = Node<ServiceNodeData, 'service'>;
export type ArchEdge = Edge<{ label?: string; edgeType: 'http' | 'event' | 'storage' | 'browser' }>;

export const initialNodes: ArchNode[] = [
  {
    id: 'browser',
    type: 'service',
    position: { x: 600, y: 0 },
    data: {
      label: 'User Browser',
      subtitle: 'Client',
      color: 'blue',
      description: 'The end-user\'s browser runs the React SPA delivered by Azure Static Web Apps. All routing is client-side; the SWA config rewrites every path to index.html for deep-link support.',
      techStack: ['Chrome', 'Firefox', 'Safari'],
      endpoints: [],
      designDocs: [],
      repoUrl: '',
    },
  },
  {
    id: 'my-website',
    type: 'service',
    position: { x: 600, y: 220 },
    data: {
      label: 'my-website',
      subtitle: 'Azure Static Web Apps',
      color: 'blue',
      description: '17 routes across these feature areas: landing, Digits, momentum finder, trail finder, dashboard, ideas board, learning plan, writing, diary, today (checklist, daily links, review cards), and an internal /architecture wiki. MSAL is used only by the dashboard and ideas board (both EasyAuth-backed); writing/diary/learning-plan/today send a raw Google ID token that each backend validates application-side instead.',
      techStack: ['React 18', 'TypeScript', 'React Router v6', 'Framer Motion', 'MSAL', '@xyflow/react', 'Azure Static Web Apps'],
      endpoints: [
        { label: 'src/index.js — router', href: 'https://github.com/skarumbu/my-website/blob/main/src/index.js' },
        { label: 'src/Ideas.tsx — ideas board', href: 'https://github.com/skarumbu/my-website/blob/main/src/Ideas.tsx' },
        { label: 'src/Dashboard.tsx', href: 'https://github.com/skarumbu/my-website/blob/main/src/Dashboard.tsx' },
        { label: 'src/TodayPage.tsx — /today', href: 'https://github.com/skarumbu/my-website/blob/main/src/TodayPage.tsx' },
        { label: 'public/staticwebapp.config.json', href: 'https://github.com/skarumbu/my-website/blob/main/public/staticwebapp.config.json' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/my-website',
    },
  },
  {
    id: 'digits',
    type: 'service',
    position: { x: 0, y: 440 },
    data: {
      label: 'digits',
      subtitle: 'Azure Functions · Python 3.11',
      color: 'green',
      description: 'Generates and serves daily Digits math puzzles. The solver uses depth-first search with integer division constraints. Writes a metrics row to Table Storage on each request.',
      techStack: ['Azure Functions v2', 'Python 3.11', 'Azure Table Storage'],
      endpoints: [
        { label: 'GET /api/DigitsGetter', href: 'https://github.com/skarumbu/digits/blob/main/function_app.py' },
        { label: 'digits_creator.py — puzzle generation', href: 'https://github.com/skarumbu/digits/blob/main/digits_creator.py' },
        { label: 'digits_solver.py — DFS solver', href: 'https://github.com/skarumbu/digits/blob/main/digits_solver.py' },
        { label: 'nodes.py — grid/adjacency logic', href: 'https://github.com/skarumbu/digits/blob/main/nodes.py' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/digits',
    },
  },
  {
    id: 'momentum_finder',
    type: 'service',
    position: { x: 300, y: 440 },
    data: {
      label: 'momentum_finder',
      subtitle: 'Azure Container Apps · FastAPI',
      color: 'orange',
      description: 'Analyses live NBA game data to surface momentum shifts, scored by three pre-trained scikit-learn models (home-run, away-run, win-probability) downloaded from Blob Storage at startup. Scales 0–10 replicas. A model_trainer.py exists for retraining, but no CI workflow currently automates it.',
      techStack: ['FastAPI', 'Python 3.10', 'Azure Container Apps', 'Log Analytics', 'scikit-learn', 'pandas', 'joblib', 'Azure Blob Storage', 'nba_api'],
      endpoints: [
        { label: 'GET /get-current-games', href: 'https://github.com/skarumbu/momentum_finder/blob/main/server.py' },
        { label: 'GET /get-momentum', href: 'https://github.com/skarumbu/momentum_finder/blob/main/server.py' },
        { label: 'GET /health', href: 'https://github.com/skarumbu/momentum_finder/blob/main/server.py' },
        { label: 'model_trainer.py', href: 'https://github.com/skarumbu/momentum_finder/blob/main/model_trainer.py' },
        { label: 'retriever.py — nba_api integration', href: 'https://github.com/skarumbu/momentum_finder/blob/main/retriever.py' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/momentum_finder',
    },
  },
  {
    id: 'trail_finder',
    type: 'service',
    position: { x: 600, y: 440 },
    data: {
      label: 'trail_finder',
      subtitle: 'Azure Container Apps · FastAPI',
      color: 'blue',
      description: 'Given a city, fetches trails via Google Places, weather from Open-Meteo (today through the coming weekend), and AllTrails snippets via Google Custom Search, then synthesizes conditions with Azure OpenAI. In-memory cache keyed by city+date — resets at UTC midnight.',
      techStack: ['FastAPI', 'Python 3.11', 'Azure Container Apps', 'Google Places API', 'Open-Meteo', 'Azure OpenAI', 'Log Analytics'],
      endpoints: [
        { label: 'GET /get-trail-recommendations', href: 'https://github.com/skarumbu/trail_finder/blob/main/main.py' },
        { label: 'GET /autocomplete', href: 'https://github.com/skarumbu/trail_finder/blob/main/main.py' },
        { label: 'GET /health', href: 'https://github.com/skarumbu/trail_finder/blob/main/main.py' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/trail_finder',
    },
  },
  {
    id: 'ideas_api',
    type: 'service',
    position: { x: 900, y: 440 },
    data: {
      label: 'ideas-api',
      subtitle: 'Azure Functions · Python 3.11',
      color: 'purple',
      description: 'Stores and serves AI-generated feature ideas in Azure Table Storage. Accepts EasyAuth (browser JWT) or a machine write-key. Triggers the ideas-bot Container App Job.',
      techStack: ['Azure Functions v2', 'Python 3.11', 'Azure Table Storage', 'EasyAuth', 'Managed Identity'],
      endpoints: [
        { label: 'GET /api/ideas', href: 'https://github.com/skarumbu/ideas-api/blob/main/function_app.py#L97' },
        { label: 'POST /api/ideas', href: 'https://github.com/skarumbu/ideas-api/blob/main/function_app.py#L116' },
        { label: 'PATCH /api/ideas/{id}/bot', href: 'https://github.com/skarumbu/ideas-api/blob/main/function_app.py#L194' },
        { label: 'POST /api/ideas/{id}/run-bot', href: 'https://github.com/skarumbu/ideas-api/blob/main/function_app.py#L222' },
        { label: 'GET/POST /api/projects', href: 'https://github.com/skarumbu/ideas-api/blob/main/function_app.py#L61' },
        { label: 'ideas.py — Table Storage CRUD', href: 'https://github.com/skarumbu/ideas-api/blob/main/ideas.py' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/ideas-api',
    },
  },
  {
    id: 'dashboard_api',
    type: 'service',
    position: { x: 1200, y: 440 },
    data: {
      label: 'dashboard_api',
      subtitle: 'Azure Functions · Python 3.11',
      color: 'purple',
      description: 'Aggregates health checks, Log Analytics metrics, Digits Table Storage metrics, and Azure Cost Management MTD spend across all services. Uses Managed Identity — no stored credentials.',
      techStack: ['Azure Functions v2', 'Python 3.11', 'Managed Identity', 'azure-monitor-query', 'Azure Cost Management', 'azure-mgmt-resourcegraph'],
      endpoints: [
        { label: 'GET /api/DashboardGetter', href: 'https://github.com/skarumbu/dashboard_api/blob/main/function_app.py' },
        { label: 'GET /api/discover', href: 'https://github.com/skarumbu/dashboard_api/blob/main/function_app.py' },
        { label: 'GET /api/apps', href: 'https://github.com/skarumbu/dashboard_api/blob/main/function_app.py' },
        { label: 'POST /api/apps', href: 'https://github.com/skarumbu/dashboard_api/blob/main/function_app.py' },
        { label: 'discovery.py — Resource Graph OBO', href: 'https://github.com/skarumbu/dashboard_api/blob/main/discovery.py' },
        { label: 'health_checks.py', href: 'https://github.com/skarumbu/dashboard_api/blob/main/health_checks.py' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/dashboard_api',
    },
  },
  {
    id: 'ideas_bot',
    type: 'service',
    position: { x: 900, y: 660 },
    data: {
      label: 'ideas-bot',
      subtitle: 'Container App Job · Python',
      color: 'gray',
      description: 'Autonomous agent triggered by ideas-api. Clones the target repo, runs a tool-use loop against a configurable Azure OpenAI model (default: o3-mini, overridable per-run to gpt-4o/gpt-5-mini/gpt-4.1) to implement the feature, commits, and opens a draft PR on GitHub.',
      techStack: ['Python 3.11', 'Azure Container App Job', 'Azure OpenAI (o3-mini default)', 'GitHub API'],
      endpoints: [
        { label: 'bot.py — main orchestrator', href: 'https://github.com/skarumbu/ideas-bot/blob/main/bot.py' },
        { label: 'Dockerfile', href: 'https://github.com/skarumbu/ideas-bot/blob/main/Dockerfile' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/ideas-bot',
    },
  },
  {
    id: 'posts_api',
    type: 'service',
    position: { x: 0, y: 660 },
    data: {
      label: 'posts-api',
      subtitle: 'Azure Functions · Python 3.11',
      color: 'green',
      description: 'Manages content sections (writing, diary) — both private, both stored entirely via history-api (not GitHub). Google ID token auth with per-item ownership and an allowlist gating writes.',
      techStack: ['Azure Functions v2', 'Python 3.11', 'history-api (HistoryApiStorage)', 'google-auth'],
      endpoints: [
        { label: 'GET/POST /api/sections/{section}/items', href: 'https://github.com/skarumbu/posts-api/blob/main/function_app.py' },
        { label: 'PUT/DELETE /api/sections/{section}/items/{slug}', href: 'https://github.com/skarumbu/posts-api/blob/main/function_app.py' },
        { label: 'GET /api/sections/{section}/items/{slug}/versions[/{id}]', href: 'https://github.com/skarumbu/posts-api/blob/main/function_app.py' },
        { label: 'GET /api/sections/{section}/items/{slug}/versions/{v1}/diff/{v2}', href: 'https://github.com/skarumbu/posts-api/blob/main/function_app.py' },
        { label: 'sections.py — section config', href: 'https://github.com/skarumbu/posts-api/blob/main/sections.py' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/posts-api',
    },
  },
  {
    id: 'daily_api',
    type: 'service',
    position: { x: -300, y: 660 },
    data: {
      label: 'daily-api',
      subtitle: 'Azure Functions · Python 3.12 · Flex Consumption',
      color: 'green',
      description: 'Backs my-website\'s /today page: daily checklist, daily links, and spaced-repetition review cards (SM-2-style scheduling, optional card back). Three Table Storage tables (DailyChecklist, DailyLinks, DailyCards) in its own storage account, partitioned by the signed-in user\'s email. Self-verifies Google ID tokens plus an ALLOWED_WRITERS allowlist. Split out of posts-api on 2026-10-22.',
      techStack: ['Azure Functions (Python v2 model)', 'Python 3.12', 'Azure Table Storage', 'google-auth', 'Flex Consumption (FC1)'],
      endpoints: [
        { label: 'GET /api/health', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'GET/POST /api/daily/checklist', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'POST /api/daily/checklist/{id}/toggle', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'GET/PUT /api/daily/links/config', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'GET /api/daily/links/opened', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'POST /api/daily/links/{id}/opened', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'GET /api/daily/cards/due', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'POST /api/daily/cards', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'POST /api/daily/cards/{id}/review', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'DELETE /api/daily/cards/{id}', href: 'https://github.com/skarumbu/daily-api/blob/main/function_app.py' },
        { label: 'auth.py — Google ID token + allowlist', href: 'https://github.com/skarumbu/daily-api/blob/main/auth.py' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/daily-api',
    },
  },
  {
    id: 'learning_plan_api',
    type: 'service',
    position: { x: 300, y: 660 },
    data: {
      label: 'learning-plan-api',
      subtitle: 'Azure Functions · Python 3.11',
      color: 'purple',
      description: 'Generates structured learning plans via Azure OpenAI and stores them per user in Azure Table Storage, partitioned by the Google sub claim. Google ID tokens verified server-side on every request.',
      techStack: ['Azure Functions v2', 'Python 3.11', 'Azure Table Storage', 'Azure OpenAI', 'Google OAuth'],
      endpoints: [
        { label: 'GET/POST /api/plans', href: 'https://github.com/skarumbu/learning-plan-api/blob/main/function_app.py' },
        { label: 'POST /api/plans/generate', href: 'https://github.com/skarumbu/learning-plan-api/blob/main/function_app.py' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/learning-plan-api',
    },
  },
  {
    id: 'azure_infrastructure',
    type: 'service',
    position: { x: 1200, y: 660 },
    data: {
      label: 'azure-infrastructure',
      subtitle: 'Bicep · Subscription scope',
      color: 'gray',
      description: 'Provisions most Azure resources: Static Web App, Container Registry, shared Container App Environment (+ Log Analytics workspace), Container Apps, Function Apps (incl. history-api and daily-api, both on Flex Consumption FC1 — new function apps must use Flex because a new Linux Y1 plan is refused in this resource group), three separate Cognitive Services/OpenAI accounts, and all RBAC role assignments. running-app is a separate top-level template in this repo, with its own resource group/region — main.bicep never invokes it.',
      techStack: ['Azure Bicep', 'Azure RBAC', 'Managed Identity', 'Azure Container Registry', 'Azure Cognitive Services'],
      endpoints: [],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/azure-infrastructure',
    },
  },
  {
    id: 'history_api',
    type: 'service',
    position: { x: 600, y: 660 },
    data: {
      label: 'history-api',
      subtitle: 'Azure Functions (Go) · Flex Consumption',
      color: 'green',
      description: 'Shared version-history and diffing service. Sole storage backend for posts-api\'s writing/diary sections and for my-website\'s /architecture wiki — not an optional versioning add-on for either.',
      techStack: ['Go 1.25', 'Azure Functions (golang-worker)', 'Azure Table Storage', 'Azure Blob Storage', 'EasyAuth'],
      endpoints: [
        { label: 'POST /documents/{id}/versions', href: 'https://github.com/skarumbu/history-api/blob/main/handlers/save_version.go' },
        { label: 'GET /documents/{id}/versions/{v1}/diff/{v2}', href: 'https://github.com/skarumbu/history-api/blob/main/handlers/diff_versions.go' },
        { label: 'GET /sections/{section}/documents/{slug}', href: 'https://github.com/skarumbu/history-api/blob/main/handlers/get_document.go' },
        { label: 'PUT /sections/{section}/settings — visibility', href: 'https://github.com/skarumbu/history-api/blob/main/handlers/update_section_settings.go' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/history-api',
    },
  },
  {
    id: 'open_meteo',
    type: 'service',
    position: { x: 600, y: 880 },
    data: {
      label: 'Open-Meteo',
      subtitle: 'External · Weather API',
      color: 'gray',
      description: 'Free weather API, no key required. trail-finder fetches today-through-weekend forecasts for the searched city.',
      techStack: ['REST API'],
      endpoints: [],
      designDocs: [],
      repoUrl: '',
    },
  },
  {
    id: 'google_apis',
    type: 'service',
    position: { x: 300, y: 880 },
    data: {
      label: 'Google APIs',
      subtitle: 'External · Places, Custom Search, Identity',
      color: 'gray',
      description: 'trail-finder calls Google Places (trail search) and Google Custom Search (AllTrails snippets); running-app and posts-api/learning-plan-api/daily-api rely on Google Identity Services / ID-token verification for auth.',
      techStack: ['Google Places API', 'Google Custom Search', 'Google Identity Services'],
      endpoints: [],
      designDocs: [],
      repoUrl: '',
    },
  },
  {
    id: 'azure_openai',
    type: 'service',
    position: { x: 900, y: 880 },
    data: {
      label: 'Azure OpenAI',
      subtitle: 'External · Cognitive Services (multiple deployments)',
      color: 'gray',
      description: 'Three separate Cognitive Services accounts, not one shared deployment: trail-finder-foundry (gear/condition synthesis), ideas-bot\'s account (o3-mini default, gpt-4o/gpt-5-mini/gpt-4.1/gpt-5.3-codex available), and learning-plan-api\'s own account.',
      techStack: ['Azure OpenAI', 'Azure Cognitive Services'],
      endpoints: [],
      designDocs: [],
      repoUrl: '',
    },
  },
  {
    id: 'github_api',
    type: 'service',
    position: { x: 1200, y: 880 },
    data: {
      label: 'GitHub API',
      subtitle: 'External',
      color: 'gray',
      description: 'ideas-bot clones repos, commits, and opens draft PRs; dashboard-api optionally polls GitHub Actions run status and open PRs per registered app (both require GITHUB_TOKEN, degrade gracefully if unset).',
      techStack: ['GitHub REST API'],
      endpoints: [],
      designDocs: [],
      repoUrl: '',
    },
  },
  {
    id: 'nba_stats',
    type: 'service',
    position: { x: 0, y: 880 },
    data: {
      label: 'NBA Stats (nba_api)',
      subtitle: 'External',
      color: 'gray',
      description: 'momentum-finder pulls live game scores and play-by-play data via the nba_api Python client.',
      techStack: ['nba_api'],
      endpoints: [],
      designDocs: [],
      repoUrl: '',
    },
  },
  {
    id: 'running_app',
    type: 'service',
    position: { x: 0, y: 220 },
    data: {
      label: 'running-app',
      subtitle: 'Azure Static Web Apps + Functions · Python/React',
      color: 'blue',
      description: 'Personal running tracker: a React SPA (also ships as a native iOS app via Capacitor) records GPS runs client-side and stores them via its own Python Azure Functions API and dedicated PostgreSQL database. Shows run history, personal bests, and automatic badges. History cards show plain distance/duration/pace/name — there is no route-shape classification, reverse geocoding, or weather lookup anywhere in the codebase.',
      techStack: ['React 18/19', 'TypeScript', 'Capacitor 7 (iOS)', 'Azure Static Web Apps', 'Azure Functions v2', 'Python', 'pg8000', 'PostgreSQL', 'Google Identity Services'],
      endpoints: [
        { label: 'GET/POST /api/runs', href: 'https://github.com/skarumbu/running-app/blob/main/api/function_app.py' },
        { label: 'GET/DELETE /api/runs/{id}', href: 'https://github.com/skarumbu/running-app/blob/main/api/function_app.py' },
        { label: 'GET /api/runs/bests', href: 'https://github.com/skarumbu/running-app/blob/main/api/function_app.py' },
        { label: 'GET /api/badges', href: 'https://github.com/skarumbu/running-app/blob/main/api/function_app.py' },
      ],
      designDocs: [],
      repoUrl: 'https://github.com/skarumbu/running-app',
    },
  },
];

// Package keys used elsewhere (architecture-pages.json, packageTemplates' PACKAGE_TEMPLATES) are
// always hyphenated (e.g. "momentum-finder"), but several node ids here use underscores instead
// ("momentum_finder") because they were named after their GitHub repo folder, not the package key.
// Normalize to hyphens so this map's keys always match the hyphenated package key convention.
// Package keys don't always match their GitHub repo name either (e.g. `momentum-finder` is hosted
// at `github.com/skarumbu/momentum_finder`) — derive the mapping from the diagram's own repoUrl
// data so it can never drift out of sync with it, rather than reconstructing URLs from the key.
export const repoUrlByPackage: Record<string, string> = Object.fromEntries(
  initialNodes.filter(n => n.data.repoUrl).map(n => [n.id.replace(/_/g, '-'), n.data.repoUrl])
);

export const initialEdges: ArchEdge[] = [
  {
    id: 'e-browser-spa',
    source: 'browser',
    target: 'my-website',
    data: { label: 'HTTPS', edgeType: 'browser' },
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#58a6ff', strokeWidth: 2 },
    label: 'HTTPS',
  },
  {
    id: 'e-spa-digits',
    source: 'my-website',
    target: 'digits',
    data: { label: 'GET /api/DigitsGetter', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#3fb950' },
    label: 'GET /api/DigitsGetter',
  },
  {
    id: 'e-spa-momentum',
    source: 'my-website',
    target: 'momentum_finder',
    data: { label: 'GET /get-current-games', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#d29922' },
    label: 'GET /get-current-games',
  },
  {
    id: 'e-spa-trail',
    source: 'my-website',
    target: 'trail_finder',
    data: { label: 'GET /get-trail-recommendations', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#58a6ff' },
    label: 'GET /get-trail-recommendations',
  },
  {
    id: 'e-spa-ideas',
    source: 'my-website',
    target: 'ideas_api',
    data: { label: 'GET /api/ideas', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#bc8cff' },
    label: 'GET /api/ideas',
  },
  {
    id: 'e-spa-dashboard',
    source: 'my-website',
    target: 'dashboard_api',
    data: { label: 'GET /api/DashboardGetter', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#bc8cff' },
    label: 'GET /api/DashboardGetter',
  },
  {
    id: 'e-spa-posts',
    source: 'my-website',
    target: 'posts_api',
    data: { label: 'GET/POST /api/sections/{section}/items', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#3fb950' },
    label: 'GET/POST /api/sections/{section}/items',
  },
  {
    id: 'e-spa-daily',
    source: 'my-website',
    target: 'daily_api',
    data: { label: '/api/daily/* (checklist, links, cards)', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#3fb950' },
    label: '/api/daily/* (checklist, links, cards)',
  },
  {
    id: 'e-spa-learning-plan',
    source: 'my-website',
    target: 'learning_plan_api',
    data: { label: 'GET/POST /api/plans', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#bc8cff' },
    label: 'GET/POST /api/plans',
  },
  {
    id: 'e-ideas-bot',
    source: 'ideas_api',
    target: 'ideas_bot',
    data: { label: 'trigger job', edgeType: 'event' },
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#bc8cff', strokeDasharray: '5 3' },
    label: 'trigger job',
  },
  {
    id: 'e-dash-momentum',
    source: 'dashboard_api',
    target: 'momentum_finder',
    data: { label: 'health check', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e', strokeDasharray: '4 3' },
    label: 'health check',
  },
  {
    id: 'e-dash-trail',
    source: 'dashboard_api',
    target: 'trail_finder',
    data: { label: 'health check', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e', strokeDasharray: '4 3' },
    label: 'health check',
  },
  {
    id: 'e-infra-spa',
    source: 'azure_infrastructure',
    target: 'my-website',
    data: { edgeType: 'storage' },
    type: 'smoothstep',
    style: { stroke: '#30363d', strokeDasharray: '6 3', opacity: 0.5 },
    label: 'provisions',
  },
  {
    id: 'e-infra-digits',
    source: 'azure_infrastructure',
    target: 'digits',
    data: { edgeType: 'storage' },
    type: 'smoothstep',
    style: { stroke: '#30363d', strokeDasharray: '6 3', opacity: 0.5 },
  },
  {
    id: 'e-infra-ideas',
    source: 'azure_infrastructure',
    target: 'ideas_api',
    data: { edgeType: 'storage' },
    type: 'smoothstep',
    style: { stroke: '#30363d', strokeDasharray: '6 3', opacity: 0.5 },
  },
  {
    id: 'e-infra-dashboard',
    source: 'azure_infrastructure',
    target: 'dashboard_api',
    data: { edgeType: 'storage' },
    type: 'smoothstep',
    style: { stroke: '#30363d', strokeDasharray: '6 3', opacity: 0.5 },
  },
  {
    id: 'e-infra-history-api',
    source: 'azure_infrastructure',
    target: 'history_api',
    data: { edgeType: 'storage' },
    type: 'smoothstep',
    style: { stroke: '#30363d', strokeDasharray: '6 3', opacity: 0.5 },
    label: 'provisions',
  },
  {
    id: 'e-infra-daily-api',
    source: 'azure_infrastructure',
    target: 'daily_api',
    data: { edgeType: 'storage' },
    type: 'smoothstep',
    style: { stroke: '#30363d', strokeDasharray: '6 3', opacity: 0.5 },
    label: 'provisions',
  },
  {
    id: 'e-infra-running-app',
    source: 'azure_infrastructure',
    target: 'running_app',
    data: { label: 'provisions (separate template)', edgeType: 'storage' },
    type: 'smoothstep',
    style: { stroke: '#30363d', strokeDasharray: '6 3', opacity: 0.5 },
    label: 'provisions (separate template)',
  },
  {
    id: 'e-browser-running-app',
    source: 'browser',
    target: 'running_app',
    data: { label: 'HTTPS', edgeType: 'browser' },
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#58a6ff', strokeWidth: 2 },
    label: 'HTTPS',
  },
  {
    id: 'e-spa-history-api',
    source: 'my-website',
    target: 'history_api',
    data: { label: 'GET /sections/architecture/documents', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#3fb950' },
    label: 'GET /sections/architecture/documents',
  },
  {
    id: 'e-posts-history-api',
    source: 'posts_api',
    target: 'history_api',
    data: { label: 'all storage traffic (GET/POST /documents/{id}/versions)', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#3fb950' },
    label: 'all storage traffic',
  },
  {
    id: 'e-trail-open-meteo',
    source: 'trail_finder',
    target: 'open_meteo',
    data: { label: 'weather forecast', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e' },
    label: 'weather forecast',
  },
  {
    id: 'e-trail-google',
    source: 'trail_finder',
    target: 'google_apis',
    data: { label: 'Places + Custom Search', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e' },
    label: 'Places + Custom Search',
  },
  {
    id: 'e-trail-openai',
    source: 'trail_finder',
    target: 'azure_openai',
    data: { label: 'condition synthesis', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e' },
    label: 'condition synthesis',
  },
  {
    id: 'e-momentum-nba',
    source: 'momentum_finder',
    target: 'nba_stats',
    data: { label: 'live scores + play-by-play', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e' },
    label: 'live scores + play-by-play',
  },
  {
    id: 'e-ideasbot-openai',
    source: 'ideas_bot',
    target: 'azure_openai',
    data: { label: 'tool-use loop (o3-mini default)', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e' },
    label: 'tool-use loop (o3-mini default)',
  },
  {
    id: 'e-ideasbot-github',
    source: 'ideas_bot',
    target: 'github_api',
    data: { label: 'clone · commit · open PR', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e' },
    label: 'clone · commit · open PR',
  },
  {
    id: 'e-dashboard-github',
    source: 'dashboard_api',
    target: 'github_api',
    data: { label: 'Actions status + open PRs (optional)', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e', strokeDasharray: '4 3' },
    label: 'Actions status + open PRs (optional)',
  },
  {
    id: 'e-learningplan-openai',
    source: 'learning_plan_api',
    target: 'azure_openai',
    data: { label: 'plan generation', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e' },
    label: 'plan generation',
  },
  {
    id: 'e-runningapp-google',
    source: 'running_app',
    target: 'google_apis',
    data: { label: 'Sign-In + tokeninfo verification', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e' },
    label: 'Sign-In + tokeninfo verification',
  },
  {
    id: 'e-posts-google',
    source: 'posts_api',
    target: 'google_apis',
    data: { label: 'ID token verification', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e', strokeDasharray: '4 3' },
    label: 'ID token verification',
  },
  {
    id: 'e-learningplan-google',
    source: 'learning_plan_api',
    target: 'google_apis',
    data: { label: 'ID token verification', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e', strokeDasharray: '4 3' },
    label: 'ID token verification',
  },
  {
    id: 'e-daily-google',
    source: 'daily_api',
    target: 'google_apis',
    data: { label: 'ID-token verify', edgeType: 'http' },
    type: 'smoothstep',
    style: { stroke: '#8b949e', strokeDasharray: '4 3' },
    label: 'ID-token verify',
  },
];
